import { io } from 'socket.io-client';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.eventListeners = new Map();
    this.currentMonth = null;
  }

  connect(token) {
    if (this.socket && this.isConnected) {
      console.log('🔌 WebSocket already connected');
      return;
    }

    const serverUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
    const isDev = process.env.NODE_ENV !== 'production';

    if (isDev) {
      console.log('🔌 Connecting to WebSocket server:', serverUrl);
      console.log('🔌 Token provided:', token ? 'Yes' : 'No');
      console.log('🔌 Token length:', token ? token.length : 0);
    }

    this.socket = io(serverUrl, {
      auth: {
        token: token
      },
      transports: ['websocket', 'polling'],
      timeout: 20000,
      forceNew: true
    });

    this.setupEventHandlers(serverUrl);
  }

  setupEventHandlers(serverUrl) {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('🔌 WebSocket connected successfully');
      console.log('🔌 Socket ID:', this.socket.id);
      console.log('🔌 Server URL:', serverUrl);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      
      // Emit custom event for connection
      this.emit('connected');
      
      // Update presence status
      this.updatePresence('online', 'work-schedule');
    });

    this.socket.on('disconnect', (reason) => {
      console.log('🔌 WebSocket disconnected:', reason);
      this.isConnected = false;
      
      // Emit custom event for disconnection
      this.emit('disconnected', reason);
      
      // Attempt to reconnect if not manually disconnected
      if (reason !== 'io client disconnect') {
        this.attemptReconnect();
      }
    });

    this.socket.on('connect_error', (error) => {
      if (process.env.NODE_ENV !== 'production') {
        console.error('🔌 WebSocket connection error:', error);
      }
      this.isConnected = false;

      const message = (error && (error.message || String(error))) || '';
      const isAuthFailure =
        /authentication/i.test(message) ||
        /auth/i.test(message) ||
        /token/i.test(message) ||
        /jwt/i.test(message) ||
        /unauthorized/i.test(message);

      this.emit('connection-error', error);

      if (isAuthFailure) {
        // Stop retrying with an expired/invalid token — let the app force logout
        this.reconnectAttempts = this.maxReconnectAttempts;
        this.emit('auth-failure', error);
        return;
      }

      this.attemptReconnect();
    });

    // Work Schedule Events
    this.socket.on('schedule-updated', (data) => {
      console.log('📅 Received schedule update:', data);
      console.log('📅 Socket ID:', this.socket.id);
      console.log('📅 Event data type:', typeof data);
      this.emit('schedule-updated', data);
    });

    this.socket.on('schedule-deleted', (data) => {
      console.log('📅 Received schedule deletion:', data);
      console.log('📅 Socket ID:', this.socket.id);
      this.emit('schedule-deleted', data);
    });

    this.socket.on('change-request-updated', (data) => {
      console.log('📝 Received change request update:', data);
      console.log('📝 Socket ID:', this.socket.id);
      this.emit('change-request-updated', data);
    });


    this.socket.on('approval-updated', (data) => {
      console.log('✅ Received approval update:', data);
      console.log('✅ Socket ID:', this.socket.id);
      this.emit('approval-updated', data);
    });

    // Presence Events
    this.socket.on('presence-updated', (data) => {
      console.log('👥 Received presence update:', data);
      this.emit('presence-updated', data);
    });

    this.socket.on('presence-data', (data) => {
      console.log('👥 Received presence data:', data);
      this.emit('presence-data', data);
    });

    // Editing Events
    this.socket.on('user-editing', (data) => {
      console.log('✏️ User started editing:', data);
      this.emit('user-editing', data);
    });

    this.socket.on('user-stopped-editing', (data) => {
      console.log('✏️ User stopped editing:', data);
      this.emit('user-stopped-editing', data);
    });

    // Notification Events
    this.socket.on('notification-created', (data) => {
      console.log('🔔 Received new notification:', data);
      this.emit('notification-created', data);
    });

    // Task Events
    this.socket.on('task-updated', (data) => {
      console.log('📋 Received task update:', data);
      this.emit('task-updated', data);
    });
  }

  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('🔌 Max reconnection attempts reached');
      this.emit('max-reconnect-attempts-reached');
      return;
    }

    this.reconnectAttempts++;
    console.log(`🔌 Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${this.reconnectDelay}ms`);
    
    setTimeout(() => {
      if (this.socket && !this.isConnected) {
        this.socket.connect();
      }
    }, this.reconnectDelay);

    // Exponential backoff
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000); // Max 30 seconds
  }

  disconnect() {
    if (this.socket) {
      console.log('🔌 Manually disconnecting WebSocket');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  // Event system for components to listen to WebSocket events
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.eventListeners.has(event)) {
      const listeners = this.eventListeners.get(event);
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in WebSocket event listener for ${event}:`, error);
        }
      });
    }
  }

  // Work Schedule Methods
  joinMonth(year, month) {
    if (this.socket && this.isConnected) {
      this.currentMonth = { year, month };
      this.socket.emit('join-month', { year, month });
      console.log(`📅 Joined month view: ${year}-${month}`);
    }
  }

  leaveMonth(year, month) {
    if (this.socket && this.isConnected) {
      this.socket.emit('leave-month', { year, month });
      console.log(`📅 Left month view: ${year}-${month}`);
    }
  }

  startEditing(userId, date, cellType = 'schedule') {
    if (this.socket && this.isConnected) {
      this.socket.emit('start-editing', { userId, date, cellType });
      console.log(`✏️ Started editing: ${userId}-${date}`);
    }
  }

  stopEditing(userId, date) {
    if (this.socket && this.isConnected) {
      this.socket.emit('stop-editing', { userId, date });
      console.log(`✏️ Stopped editing: ${userId}-${date}`);
    }
  }

  // Presence Methods
  updatePresence(status, currentPage = null) {
    if (this.socket && this.isConnected) {
      this.socket.emit('update-presence', { status, currentPage });
    }
  }

  getPresence() {
    if (this.socket && this.isConnected) {
      this.socket.emit('get-presence');
    }
  }

  // Project Methods
  joinProject(projectId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('join-project', { projectId });
      console.log(`📋 Joined project room: ${projectId}`);
    }
  }

  leaveProject(projectId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('leave-project', { projectId });
      console.log(`📋 Left project room: ${projectId}`);
    }
  }

  // Utility Methods
  isConnectedToServer() {
    return this.isConnected && this.socket;
  }

  getConnectionStatus() {
    return {
      connected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      currentMonth: this.currentMonth
    };
  }
}

// Create singleton instance
const webSocketService = new WebSocketService();

export default webSocketService;
